/**
 * Lightweight dev-only logger + production error-reporter.
 *
 * Replaces scattered `console.log` / `console.warn` / `console.error` calls
 * with a single indirection so production builds can:
 *   1. Silence all `.info()` / `.debug()` chatter (default),
 *   2. Keep `.warn()` / `.error()` visible so browser DevTools + bug reports
 *      still surface real issues,
 *   3. Batch-ship `.error()` payloads to `POST /api/client-errors` so we
 *      have visibility into production errors without a paid Sentry tier.
 *      Errors are queued in-memory, flushed every 4s OR when the queue
 *      hits 10 items, whichever comes first. Beforeunload also flushes
 *      via `navigator.sendBeacon` so we don't lose the last batch.
 *   4. Mirror `.error()` payloads to Sentry (when configured).
 *
 * Behaviour is controlled by `REACT_APP_LOG_LEVEL`:
 *   - 'debug'  → all levels printed
 *   - 'info'   → info, warn, error (default in development)
 *   - 'warn'   → warn + error (default in production)
 *   - 'error'  → error only
 *   - 'silent' → nothing
 *
 * Reporting is controlled by `REACT_APP_CLIENT_ERROR_REPORTING`:
 *   - 'off'  → never POST (default in development)
 *   - 'on'   → batch + POST (default in production)
 *
 * Usage:
 *   import { log } from '../lib/logger';
 *   log.info('Order created', order);
 *   log.warn('Fallback shipping rate used');
 *   log.error('Upload failed', err);
 */
import { Sentry, sentryEnabled } from '../instrument';
const LEVELS = { debug: 10, info: 20, warn: 30, error: 40, silent: 50 };

const resolveLevel = () => {
  const fromEnv = (process.env.REACT_APP_LOG_LEVEL || '').toLowerCase();
  if (fromEnv in LEVELS) return LEVELS[fromEnv];
  return process.env.NODE_ENV === 'production' ? LEVELS.warn : LEVELS.info;
};

const currentLevel = resolveLevel();

const make = (fn, threshold) => (...args) => {
  if (currentLevel <= threshold) fn(...args);
};

// ---------------------------------------------------------------- //
// Production error reporter — batches log.error() to the backend.   //
// ---------------------------------------------------------------- //
const REPORT_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ''}/api/client-errors`;
const REPORTING_DEFAULT = process.env.NODE_ENV === 'production' ? 'on' : 'off';
const REPORTING_FLAG = (
  process.env.REACT_APP_CLIENT_ERROR_REPORTING || REPORTING_DEFAULT
).toLowerCase();
const REPORTING_ENABLED = REPORTING_FLAG === 'on';

const FLUSH_INTERVAL_MS = 4000;
const FLUSH_THRESHOLD = 10;
const MAX_QUEUE = 50; // hard cap so a runaway loop can't OOM us

const errorQueue = [];
let flushTimer = null;

const safeStringify = (val) => {
  try {
    if (val instanceof Error) return val.stack || `${val.name}: ${val.message}`;
    if (typeof val === 'string') return val;
    return JSON.stringify(val).slice(0, 1000);
  } catch (e) {
    return String(val);
  }
};

const buildErrorRecord = (args) => {
  const message = args.length > 0 ? safeStringify(args[0]) : 'Unknown error';
  // Treat the rest as either an Error stack (preferred) or generic
  // context. We capture both so the backend has enough to triage
  // without leaking page state.
  const errArg = args.find((a) => a instanceof Error);
  const stack = errArg ? (errArg.stack || `${errArg.name}: ${errArg.message}`) : null;
  return {
    message: message.slice(0, 500),
    stack: stack ? stack.slice(0, 4000) : null,
    url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  };
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushNow();
  }, FLUSH_INTERVAL_MS);
};

const flushNow = () => {
  if (errorQueue.length === 0 || !REPORTING_ENABLED) return;
  const batch = errorQueue.splice(0, errorQueue.length);
  const body = JSON.stringify({ errors: batch });
  // Prefer fetch (with keepalive) so DevTools shows the request and
  // the backend can return a meaningful 202 / throttle envelope.
  // Fall back to sendBeacon at unload-time which doesn't surface
  // response bodies but is fire-and-forget reliable.
  try {
    fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'include',
    }).catch(() => {
      // Swallow — never let logger errors blow up the page.
    });
  } catch {
    // ignore
  }
};

const reportError = (...args) => {
  if (!REPORTING_ENABLED) return;
  // Refuse to enqueue beyond the safety cap.
  if (errorQueue.length >= MAX_QUEUE) return;
  errorQueue.push(buildErrorRecord(args));
  if (errorQueue.length >= FLUSH_THRESHOLD) {
    flushNow();
  } else {
    scheduleFlush();
  }
};

// Hook beforeunload so the last batch of errors makes it out before
// the customer leaves the page (e.g. closes tab on a broken Place
// Order). sendBeacon is the only way to send a payload reliably here.
if (REPORTING_ENABLED && typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (errorQueue.length === 0) return;
    const body = JSON.stringify({ errors: errorQueue.splice(0, errorQueue.length) });
    try {
      navigator.sendBeacon?.(REPORT_ENDPOINT, new Blob([body], { type: 'application/json' }));
    } catch {
      // ignore
    }
  });
}

const errorAndReport = (...args) => {
  if (currentLevel <= LEVELS.error) console.error(...args);
  reportError(...args);
  // Mirror to Sentry when enabled. The static import at the top of
  // this file is safe because instrument.js itself only depends on
  // @sentry/react, never on us — no cycle.
  try {
    if (sentryEnabled()) {
      const errArg = args.find((a) => a instanceof Error);
      if (errArg) {
        Sentry.captureException(errArg, {
          contexts: {
            log_args: { extras: args.filter((a) => !(a instanceof Error)).map(safeStringify).slice(0, 5) },
          },
        });
      } else {
        Sentry.captureMessage(safeStringify(args[0] || 'Unknown error'), 'error');
      }
    }
  } catch {
    // ignore
  }
};

export const log = {
  debug: make(console.debug.bind(console), LEVELS.debug),
  info: make(console.log.bind(console), LEVELS.info),
  warn: make(console.warn.bind(console), LEVELS.warn),
  error: errorAndReport,
};

export default log;
